import { Link } from "react-router-dom";
import { ArrowRight, Leaf, Microscope, Target } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal, MaskedLines, Eyebrow } from "../components/Reveal";
import { IMG, SITE } from "../data/products";

const PILLARS = [
  {
    icon: Leaf,
    title: "Nature, honestly used",
    body: "Sweeteners from high-potency molecules, flavours drawn from familiar feeds, botanicals like curcumin and ashwagandha — nature, made reliable at the feed mill.",
  },
  {
    icon: Microscope,
    title: "Science, made practical",
    body: "The Micropearl process, thermostable granulation and controlled-release systems turn flavour chemistry into products that survive mixing, pelleting and storage.",
  },
  {
    icon: Target,
    title: "Species first",
    body: "Every formulation starts from the animal: the calf at weaning, the layer under heat stress, the pond, the nursery. Dosage and delivery follow the species, not the other way round.",
  },
];

const About = () => (
  <div data-testid="page-about">
    <Seo
      title="About Us"
      description="AURIK INGREVIA (P) Ltd. is an animal-nutrition company building palatants, sweeteners and feed additives — a division / affiliate of Gait Naurish (I) Ltd."
    />

    <section className="grain mesh-glow relative overflow-hidden pb-24 pt-40 lg:pb-32 lg:pt-48">
      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
        <Eyebrow dark>About us</Eyebrow>
        <MaskedLines
          as="h1"
          delay={0.2}
          className="mt-7 max-w-3xl font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl"
          lines={["The science of", "appetite."]}
        />
        <p className="mt-8 max-w-2xl text-base leading-relaxed text-emerald-100/70 sm:text-lg">
          AURIK INGREVIA (P) Ltd. is an animal-nutrition company dedicated to one of the
          most overlooked levers in livestock production: whether the animal chooses to
          eat. We build the palatants, sweeteners and feed additives that make good
          nutrition irresistible.
        </p>
      </div>
    </section>

    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
      <div className="grid items-start gap-14 lg:grid-cols-2">
        <Reveal>
          <div className="clip-frame overflow-hidden">
            <img src={IMG.labScientist} alt="Scientist at work in an analytical laboratory" className="h-[420px] w-full object-cover lg:h-[520px]" />
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-display text-3xl font-extrabold tracking-tight text-night sm:text-4xl">
            Built for intake, from ration to rumen.
          </h2>
          <div className="mt-6 space-y-5 text-sm leading-relaxed text-slate-600 sm:text-base">
            <p>
              Aurik Ingrevia works where nutrition meets behaviour. A ration can be
              perfectly formulated on paper — but the animal decides. Our products are
              engineered for the moment of choice: the immediate sweetness burst of
              PalatiSure 650, the milk-familiar notes of FeedLure 301, the banana cue
              that keeps a stressed calf eating through a feed transition.
            </p>
            <p>
              We formulate for the realities of the feed mill — high-temperature
              pelleting, mineral mixtures, mash and pellet lines — and for the realities
              of the farm: stress, reduced appetite, mycotoxin risk and phase changes.
              Everything ships as a free-flow powder, dosed in grams per ton of finished
              feed.
            </p>
            <p>
              The Aurik range now spans six categories — palatants and sweeteners, toxin
              management, gut health and acidifiers, emulsifiers and energy, herbal and
              nutraceutical extracts, and mineral nutrition — with new FeedLure profiles
              in development.
            </p>
          </div>

          <div className="mt-10 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-7" data-testid="about-group-card">
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.24em] text-teal-700">Group affiliation</p>
            <p className="mt-3 text-sm leading-relaxed text-slate-700">
              AURIK INGREVIA (P) Ltd. is a division / affiliate of{" "}
              <span className="font-semibold text-emerald-900">{SITE.groupCompany}</span>,
              sharing the group's commitment to practical, science-led animal nutrition.
            </p>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="border-t border-slate-200 bg-white py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <Eyebrow>Our approach</Eyebrow>
          <h2 className="mt-6 max-w-xl font-display text-3xl font-extrabold tracking-tight text-night sm:text-4xl">
            Three commitments behind every formulation
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-5 md:grid-cols-3">
          {PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08} className="h-full">
              <div className="flex h-full flex-col rounded-2xl border border-slate-200 p-8 transition-colors hover:border-teal-600/40">
                <span className="font-mono text-[11px] font-semibold text-teal-700">0{i + 1}</span>
                <p.icon className="mt-6 h-8 w-8 text-emerald-800" strokeWidth={1.5} />
                <h3 className="mt-5 font-display text-xl font-bold tracking-tight text-night">{p.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">{p.body}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-16">
          <div className="flex flex-col items-start justify-between gap-6 rounded-2xl bg-night p-9 sm:flex-row sm:items-center">
            <p className="max-w-lg font-display text-xl font-bold tracking-tight text-white sm:text-2xl">
              See the range that grew from this approach.
            </p>
            <Link
              to="/products"
              data-testid="about-cta-products"
              className="group inline-flex items-center gap-2 rounded-full bg-teal-600 px-6 py-3 font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-white transition-colors hover:bg-emerald-500"
            >
              Explore products <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  </div>
);

export default About;
